import { useEffect, useMemo, useRef, useState } from "react";

import { ArrowSquareOut, Check, X } from "@phosphor-icons/react";
import {
  Badge,
  DataRow,
  DataTable,
  IconButton,
  LoadingOverlay,
  PaginationFooter,
  buildPageList,
} from "@breakingthelines/design-system";

import {
  getContent,
  listMachineTags,
  reviewMachineTag,
  type MachineSubjectRef,
  type MachineTagDecisionValue,
  type PageInfo,
} from "@/lib/admin-api";
import { isAuthFailure } from "@/lib/http-client";

import styles from "./machine-tag-queue.module.css";

const PER_PAGE_OPTIONS = [10, 20, 50];

/**
 * content-service auto-applies at or above 0.80 and queues everything below it
 * (machineTagAutoApplyFloor in machine_subject_ref_repository.go). Every row in
 * this queue is therefore under 0.80 by construction, so the bands divide that
 * range rather than the whole [0,1].
 */
const CONFIDENCE_BANDS = [
  { value: "all", label: "Any confidence", min: 0, max: Number.POSITIVE_INFINITY },
  { value: "75-79", label: "0.75 to 0.79", min: 0.75, max: 0.8 },
  { value: "70-74", label: "0.70 to 0.74", min: 0.7, max: 0.75 },
  { value: "60-69", label: "0.60 to 0.69", min: 0.6, max: 0.7 },
  { value: "below-60", label: "Below 0.60", min: 0, max: 0.6 },
] as const;

type ConfidenceBand = (typeof CONFIDENCE_BANDS)[number]["value"];

const TABLE_COLUMNS =
  "minmax(190px, 1.3fr) minmax(170px, 1.1fr) minmax(96px, 0.5fr) minmax(150px, 0.9fr) minmax(220px, 1.7fr) minmax(120px, 0.7fr) minmax(96px, 0.5fr)";

/**
 * A ref has no id of its own: (article, entity) IS the key, server-side and
 * here, and it is what ReviewMachineSubjectRef takes.
 */
function refKey(ref: MachineSubjectRef): string {
  return `${ref.articleId}:${ref.subject?.id ?? ""}`;
}

function subjectTypeLabel(type?: string): string {
  if (!type || type === "SUBJECT_TYPE_UNSPECIFIED") return "Entity";
  return type
    .replace("SUBJECT_TYPE_", "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (char) => char.toUpperCase());
}

function formatConfidence(confidence?: number): string {
  if (typeof confidence !== "number" || Number.isNaN(confidence)) return "Unknown";
  return confidence.toFixed(2);
}

/**
 * `model` arrives provider-qualified with a prompt version appended, e.g.
 * `workers-ai:@cf/meta/llama-3.3-70b-instruct-fp8-fast#entity-tag-v2`. All
 * three parts matter when a whole generation turns out to be wrong (that is
 * what content-service's purge-machine-tags takes as its scope), so none are
 * dropped: the model leads, the prompt version sits beside it, and the full
 * string stays available on hover.
 */
function splitModel(model?: string): { model: string; prompt?: string } {
  if (!model) return { model: "Unknown model" };
  const [name, prompt] = model.split("#", 2);
  return { model: name || model, prompt: prompt || undefined };
}

function formatDate(value?: string): string {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function inBand(ref: MachineSubjectRef, band: ConfidenceBand): boolean {
  const spec = CONFIDENCE_BANDS.find((option) => option.value === band);
  if (!spec || spec.value === "all") return true;
  const confidence = ref.confidence;
  if (typeof confidence !== "number" || Number.isNaN(confidence)) return false;
  return confidence >= spec.min && confidence < spec.max;
}

/**
 * The queue read returns `article_id` and nothing else about the article, so
 * the title every row needs is a second call per article. Fetched once per id
 * for the life of the page and reused across rows and pages: one article
 * commonly carries several proposed tags, so the same id repeats down the
 * list. A failed lookup is remembered as failed rather than retried on every
 * render, and the row falls back to the id, which is still a working link.
 *
 * This is the N+1 the wire shape forces. Carrying the article title on
 * MachineSubjectRef (or on the list response) would remove it outright, which
 * is worth doing while protos#199 is still open.
 */
function useArticleTitles(articleIds: string[]): Record<string, string> {
  const [titles, setTitles] = useState<Record<string, string>>({});
  const requested = useRef(new Set<string>());
  const key = articleIds.join(",");

  useEffect(() => {
    const missing = articleIds.filter((id) => id && !requested.current.has(id));
    if (missing.length === 0) return;
    missing.forEach((id) => requested.current.add(id));

    let cancelled = false;
    Promise.all(
      missing.map((id) =>
        getContent(id)
          .then((item) => [id, item.title ?? ""] as const)
          .catch(() => [id, ""] as const),
      ),
    ).then((pairs) => {
      if (cancelled) return;
      const resolved = pairs.filter(([, title]) => title !== "");
      if (resolved.length === 0) return;
      setTitles((prev) => ({ ...prev, ...Object.fromEntries(resolved) }));
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return titles;
}

function failureMessage(error: unknown, fallback: string): string {
  if (isAuthFailure(error)) {
    // Not a tag problem and not a transient one. Machine tag review is
    // humans-only server-side, so this is either a session that has expired or
    // one without editorial access on the article.
    return "Not authorised. Machine tag review is an editorial act and content-service refuses it from service principals and from sessions without editorial access on the article. Sign in again as an admin, then retry.";
  }
  return error instanceof Error && error.message ? error.message : fallback;
}

export function MachineTagQueuePage() {
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(PER_PAGE_OPTIONS[0]);
  const [band, setBand] = useState<ConfidenceBand>("all");
  const [entityType, setEntityType] = useState("all");
  const [refs, setRefs] = useState<MachineSubjectRef[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
  const [pendingTotal, setPendingTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    // The page token is a stringified offset (see listMachineTags), so a page
    // number converts to one directly.
    const pageToken = pageIndex > 1 ? String((pageIndex - 1) * pageSize) : undefined;

    listMachineTags({ status: "MACHINE_TAG_STATUS_PENDING_REVIEW", pageSize, pageToken })
      .then((response) => {
        if (cancelled) return;
        setRefs(response.refs ?? []);
        setPageInfo(response.pageInfo ?? null);
        setPendingTotal(response.pageInfo?.totalCount ?? (response.refs ?? []).length);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setRefs([]);
        setPageInfo(null);
        setPendingTotal(null);
        setLoadError(failureMessage(error, "Failed to load the machine tag queue."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [pageIndex, pageSize]);

  const articleIds = useMemo(
    () => Array.from(new Set(refs.map((ref) => ref.articleId).filter(Boolean))),
    [refs],
  );
  const titles = useArticleTitles(articleIds);

  /**
   * Type options come from the rows on screen rather than from a fixed list.
   * Both filters are applied to the loaded page (the list RPC takes neither a
   * confidence range nor a subject type), so offering a type that cannot
   * appear on this page would only produce an empty table with no explanation.
   */
  const typeOptions = useMemo(() => {
    const present = new Set(
      refs.map((ref) => ref.subject?.type).filter((type): type is string => Boolean(type)),
    );
    return Array.from(present).sort();
  }, [refs]);

  useEffect(() => {
    if (entityType !== "all" && !typeOptions.includes(entityType)) setEntityType("all");
  }, [entityType, typeOptions]);

  const visibleRefs = useMemo(
    () =>
      refs.filter(
        (ref) => inBand(ref, band) && (entityType === "all" || ref.subject?.type === entityType),
      ),
    [refs, band, entityType],
  );

  const filtered = band !== "all" || entityType !== "all";

  const handleReview = (ref: MachineSubjectRef, decision: MachineTagDecisionValue) => {
    const key = refKey(ref);
    const index = refs.findIndex((item) => refKey(item) === key);
    if (index < 0) return;

    // Optimistic: the row leaves the queue on click, and comes back in the
    // same place if the call fails. A reviewer working a queue clicks through
    // it at speed, and a row that lingers until the round trip lands gets
    // clicked twice.
    setBusyKey(key);
    setActionError(null);
    setRefs((prev) => prev.filter((item) => refKey(item) !== key));
    setPendingTotal((prev) => (prev === null ? prev : Math.max(0, prev - 1)));

    reviewMachineTag({
      articleId: ref.articleId,
      entityId: ref.subject?.id ?? "",
      decision,
    })
      .catch((error: unknown) => {
        setRefs((prev) => {
          if (prev.some((item) => refKey(item) === key)) return prev;
          const restored = [...prev];
          restored.splice(Math.min(index, restored.length), 0, ref);
          return restored;
        });
        setPendingTotal((prev) => (prev === null ? prev : prev + 1));
        setActionError(failureMessage(error, "The review could not be recorded."));
      })
      .finally(() => {
        setBusyKey((current) => (current === key ? null : current));
      });
  };

  const totalCount = pendingTotal ?? refs.length;
  const totalPages = useMemo(() => {
    if (totalCount > 0) return Math.max(1, Math.ceil(totalCount / pageSize));
    return pageIndex;
  }, [totalCount, pageIndex, pageSize]);

  const pages = useMemo(() => buildPageList(totalPages, pageIndex), [totalPages, pageIndex]);
  const activePage = Math.min(pageIndex, totalPages);
  const hasNext = pageInfo?.hasNextPage ?? (refs.length === pageSize && refs.length > 0);

  return (
    <div className={styles.page}>
      <div>
        <h1 className={styles.title}>Machine tags</h1>
        <p className={styles.subtitle}>
          Entity tags the tagger proposed below its auto-apply confidence, held for review. A
          pending tag is invisible to readers and stays that way until it is accepted here.
          Rejecting keeps the row as an audit record and stops the tagger proposing it again.
        </p>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <select
            className={styles.select}
            aria-label="Confidence band"
            value={band}
            onChange={(event) => setBand(event.currentTarget.value as ConfidenceBand)}
          >
            {CONFIDENCE_BANDS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            className={styles.select}
            aria-label="Entity type"
            value={entityType}
            onChange={(event) => setEntityType(event.currentTarget.value)}
          >
            <option value="all">Any entity type</option>
            {typeOptions.map((option) => (
              <option key={option} value={option}>
                {subjectTypeLabel(option)}
              </option>
            ))}
          </select>
        </div>

        <p className={styles.filterNote}>
          Filters narrow the loaded page. The queue read takes neither a confidence range nor an
          entity type, so paging still walks the whole queue.
        </p>
      </div>

      {actionError ? (
        <p className={styles.actionError} role="alert">
          {actionError}
        </p>
      ) : null}

      <section className={styles.listSection}>
        {loading ? <LoadingOverlay label="Loading pending tags" /> : null}

        <DataTable
          columns={TABLE_COLUMNS}
          header={[
            "Article",
            "Proposed entity",
            "Confidence",
            "Model",
            "Evidence",
            "Tagged",
            "Actions",
          ]}
          headerClassName={styles.tableHeader}
        >
          {visibleRefs.map((ref) => {
            const key = refKey(ref);
            const busy = busyKey === key;
            const entityId = ref.subject?.id ?? "";
            const model = splitModel(ref.model);
            const title = titles[ref.articleId];

            return (
              <DataRow key={key} className={styles.tableRow}>
                <div className={styles.articleCell} data-label="Article">
                  <a className={styles.articleLink} href={`/content/${ref.articleId}`}>
                    <span className={styles.articleTitle}>{title || ref.articleId}</span>
                    <ArrowSquareOut size={12} />
                  </a>
                  {title ? <span className={styles.muted}>{ref.articleId}</span> : null}
                </div>

                <div className={styles.entityCell} data-label="Proposed entity">
                  <div className={styles.entityTop}>
                    <Badge variant="tintedNeutral">{subjectTypeLabel(ref.subject?.type)}</Badge>
                    <a
                      className={styles.entityLink}
                      href={`/entities/assets/entity?id=${encodeURIComponent(entityId)}`}
                    >
                      {ref.subject?.label || entityId}
                    </a>
                  </div>
                  <span className={styles.muted} title={entityId}>
                    {ref.subject?.slug || entityId}
                  </span>
                </div>

                <span className={styles.confidence} data-label="Confidence">
                  {formatConfidence(ref.confidence)}
                </span>

                <div className={styles.modelCell} data-label="Model">
                  <span className={styles.modelName} title={ref.model || undefined}>
                    {model.model}
                  </span>
                  {model.prompt ? <span className={styles.muted}>{model.prompt}</span> : null}
                </div>

                <div data-label="Evidence">
                  {ref.evidence ? (
                    <blockquote className={styles.evidence}>{ref.evidence}</blockquote>
                  ) : (
                    <span className={styles.evidenceEmpty}>
                      No evidence span. The confirmer quoted nothing.
                    </span>
                  )}
                </div>

                <span className={styles.date} data-label="Tagged">
                  {formatDate(ref.taggedAt)}
                </span>

                <div className={styles.actions} data-label="Actions">
                  <IconButton
                    aria-label={`Accept ${ref.subject?.label || entityId}`}
                    variant="solid"
                    size="md"
                    disabled={busy}
                    onClick={() => handleReview(ref, "MACHINE_TAG_REVIEW_DECISION_ACCEPT")}
                  >
                    <Check size={14} />
                  </IconButton>
                  <IconButton
                    aria-label={`Reject ${ref.subject?.label || entityId}`}
                    variant="ghost"
                    size="md"
                    disabled={busy}
                    onClick={() => handleReview(ref, "MACHINE_TAG_REVIEW_DECISION_REJECT")}
                  >
                    <X size={14} />
                  </IconButton>
                </div>
              </DataRow>
            );
          })}
        </DataTable>

        {!loading && loadError ? <div className={styles.emptyState}>{loadError}</div> : null}
        {!loading && !loadError && visibleRefs.length === 0 ? (
          <div className={styles.emptyState}>
            {filtered
              ? "No pending tags on this page match the filter."
              : "The queue is clear. Nothing is waiting for review."}
          </div>
        ) : null}
      </section>

      <PaginationFooter
        totalLabel={`Pending tags: ${totalCount}`}
        pages={pages}
        activePage={activePage}
        hasPrevious={pageIndex > 1}
        hasNext={hasNext}
        onPrevious={() => setPageIndex((page) => Math.max(1, page - 1))}
        onNext={() => setPageIndex((page) => page + 1)}
        perPage={pageSize}
        perPageOptions={PER_PAGE_OPTIONS}
        onPerPageChange={(option) => {
          setPageSize(option);
          setPageIndex(1);
        }}
        onPageChange={(page) => {
          if (page === activePage) return;
          setPageIndex(page);
        }}
        className={styles.pagination}
      />
    </div>
  );
}
